import { useEffect, useMemo } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';
import { orderBy } from 'lodash';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import EmptyPayment from '#client/components/EmptyPayment';
import TransactionSkeleton from '#client/components/TransactionListSkeleton';
import { REFRESH_AFTER_INSTANT_DEPOSIT } from '#client/constants/eventName';
import { EMPTY_PAYMENT_DESCRIPTION, RENDER_TRANSACTION_TYPE } from '#client/constants/transaction';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useEventListeners from '#client/hooks/useEventListeners';
import { useLogError } from '#client/hooks/useLogError';
import useMemoObject from '#client/hooks/useMemoObject';
import useSkeleton from '#client/hooks/useSkeleton';
import useToastContext from '#client/hooks/useToastContext';
import { GET_INSTANT_FUNDING_TRANSFER_ROUTINES } from '#client/queries';

import ListDebitCardPayment from './components/ListDebitCardPayment';
import { PaymentContainerStyled } from './DebitCardPaymentContainer.styles';

export default function DebitCardPaymentContainer({ cardInfo }) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { debitCardUuid } = useParams();
  const { createCommonErrorToast } = useToastContext();

  const {
    loading,
    error: paymentError,
    errors,
    data: originalData,
    refetch
  } = useQuery(GET_INSTANT_FUNDING_TRANSFER_ROUTINES, {
    variables: {
      input: {
        businessEntityUuid,
        instantFundingCardId: debitCardUuid
      }
    },
    skipCache: true,
    noThrow: true
  });

  useEventListeners([{ eventName: REFRESH_AFTER_INSTANT_DEPOSIT, eventHandler: refetch }]);

  useLogError(errors, paymentError);
  const data = useMemoObject(originalData);

  useEffect(() => {
    if (paymentError) {
      createCommonErrorToast({ error: paymentError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentError]);

  const payments = useMemo(
    () =>
      orderBy(
        data?.instantFundingTransferRoutines?.data ?? [],
        ['createdDatetime', 'updatedDatetime'],
        ['desc']
      ),
    [data]
  );

  const isHideSkeleton = (data && !loading) || paymentError;
  const [showSkeleton] = useSkeleton(isHideSkeleton);

  /**
   * Function is used for transaction list get from api is empty,
   * not found transaction or render a list transaction
   */
  const renderPaymentType = useMemo(() => {
    // The transaction is empty when no transaction return and all filters are empty
    const isEmptyPayment = payments.length === 0;

    let renderType = RENDER_TRANSACTION_TYPE.SHOW_LIST;

    if (isEmptyPayment) {
      renderType = RENDER_TRANSACTION_TYPE.EMPTY;
    }

    return renderType;
  }, [payments]);

  const listPayment = useMemo(() => {
    switch (renderPaymentType) {
      case RENDER_TRANSACTION_TYPE.EMPTY:
        return <EmptyPayment description={EMPTY_PAYMENT_DESCRIPTION.DEBIT_CARD} />;
      default:
        return (
          <ListDebitCardPayment
            cardInfo={cardInfo}
            payments={payments}
          />
        );
    }
  }, [renderPaymentType, cardInfo, payments]);

  return (
    <>
      <PaymentContainerStyled maxWidth="xl">
        {showSkeleton ? <TransactionSkeleton /> : listPayment}
      </PaymentContainerStyled>
    </>
  );
}

DebitCardPaymentContainer.propTypes = {
  cardInfo: PropTypes.shape({
    cardCompany: PropTypes.string,
    cardLastFourDigits: PropTypes.string,
    cardHolderFirstName: PropTypes.string,
    cardHolderLastName: PropTypes.string,
    cardBillingAddress: PropTypes.shape({
      address1: PropTypes.string,
      address2: PropTypes.string,
      state: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string
    })
  })
};
